import React, { type CSSProperties } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import type { IndividualSessionFeedbackData } from '../../../../types/IndividualSessionFeedbackTypes'
import { GamerFeedbackContainerBox, ProfileCardWithIndicators } from '../../../feedbackGenerator/components'
import {
  EmotionStateSection,
  EmotionVisualization,
  ImprovementIdeaVisualization
} from '../../../feedbackGenerator/components/visualizations'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import { PageWrapper } from '../../../../shared/components'
import LatestFeedbackLayoutBox from '@/features/feedbackGenerator/components/LatestFeedbackLayoutBox'
import { GlowCursorProvider } from '../../components/GlowCursor'
import { SparkleCursor } from '../../components/SparkleCursor'
import type { ProfileIndicator } from '../../../feedbackGenerator/components/profileIndicatorTypes'
import { getResponsiveGridColumns, getResponsivePaddingMUI, getResponsiveGapMUI } from '../helpers/layoutHelpers'
import { LATEST_SESSION_FEEDBACK_SECTION_IDS } from '../constants/latestSessionFeedbackSections'
import { SessionFeedbackSectionHeader } from './SessionFeedbackSectionHeader'
import { WhatsWorkingSection } from './WhatsWorkingSection'
import { WatchOutSection } from './WatchOutSection'

export interface LatestSessionFeedbackContentProps {
  feedbackData: IndividualSessionFeedbackData
  profileIndicators: ProfileIndicator[]
  profileTitle: string
  profileDescription: string
  improvementVisualization: { description: string; recommendation: string }
  sessionTimeFormatted: string
  isGlowActive: boolean
  isGlintActive: boolean
  cursorStyles: CSSProperties
  pendingSections: string[]
  onSectionReveal: (sectionId: string) => void
  onSectionGlintStateChange: (sectionId: string, state: 'idle' | 'running') => void
}

export const LatestSessionFeedbackContent: React.FC<LatestSessionFeedbackContentProps> = ({
  feedbackData,
  profileIndicators,
  profileTitle,
  profileDescription,
  improvementVisualization,
  sessionTimeFormatted,
  isGlowActive,
  isGlintActive,
  cursorStyles,
  pendingSections,
  onSectionReveal,
  onSectionGlintStateChange
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'))

  const containerPadding = getResponsivePaddingMUI('CONTAINER')
  const gridGap = getResponsiveGapMUI()

  const hasProfile = profileIndicators.length > 0
  const hasImprovementIdea = !!feedbackData.improvementIdea
  const row1GridColumns = getResponsiveGridColumns(hasProfile, hasImprovementIdea, '50%', '50%')

  const hasWhatsWorking = !!feedbackData.whatIsWorkingWell
  const hasWatchOut = !!feedbackData.whatToWatchOut
  const row2GridColumns = getResponsiveGridColumns(hasWhatsWorking, hasWatchOut, '50%', '50%')

  const hasEmotion = !!feedbackData.communicatedEmotion

  return (
    <GlowCursorProvider active={isGlowActive} showGlobalGlow>
      <SparkleCursor active={isGlowActive && isGlintActive} />
      <PageWrapper
        sx={{
          zIndex: 20,
          display: 'flex',
          boxSizing: 'border-box',
          position: 'relative',
          ...cursorStyles
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '1400px',
            mx: 'auto',
            px: containerPadding,
            py: { xs: 2, sm: 3, md: 4, lg: 4 },
            display: 'flex',
            flexDirection: 'column',
            gap: gridGap
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
            <Typography sx={{ ...INTERAX_TYPOGRAPHY.headline3, color: INTERAX_COLORS.white, textAlign: 'left' }}>
              {t('gamer.feedback.pageHeader.title', 'Session Feedback')}
            </Typography>
            <Typography sx={{ ...INTERAX_TYPOGRAPHY.body2, color: INTERAX_COLORS.thunderGrey, textAlign: 'left' }}>
              {t('gamer.feedback.pageHeader.subtitle', 'Review your latest session performance and insights')}
            </Typography>
          </Box>

          <GamerFeedbackContainerBox variant="individual" sx={{ width: '100%', maxWidth: '1400px' }}>
            <SessionFeedbackSectionHeader sessionTitle={sessionTimeFormatted} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: row1GridColumns,
                gap: gridGap,
                width: '100%',
                alignItems: 'stretch',
                paddingRight: { xs: '0px', sm: '0px', md: '20px' }
              }}
            >
              {profileIndicators.length > 0 ? (
                <ProfileCardWithIndicators
                  profileName={profileTitle}
                  description={profileDescription}
                  indicators={profileIndicators}
                />
              ) : null}

              {feedbackData.improvementIdea ? (
                <LatestFeedbackLayoutBox variant="team" sx={{ width: '100%', height: '100%', alignSelf: 'stretch' }}>
                  <ImprovementIdeaVisualization
                    title={t('gamer.feedback.improvementIdea.title', 'Try next time')}
                    description={improvementVisualization.description}
                    recommendation={improvementVisualization.recommendation}
                    variant="individual"
                  />
                </LatestFeedbackLayoutBox>
              ) : null}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: row2GridColumns,
                gap: gridGap,
                width: '100%',
                alignItems: 'stretch',
                paddingRight: { xs: '0px', sm: '0px', md: '20px' }
              }}
            >
              {feedbackData.whatIsWorkingWell ? (
                <WhatsWorkingSection
                  content={feedbackData.whatIsWorkingWell}
                  showGlowExperience={pendingSections.includes(LATEST_SESSION_FEEDBACK_SECTION_IDS.whatsWorking)}
                  onGlowExperienceComplete={() => onSectionReveal(LATEST_SESSION_FEEDBACK_SECTION_IDS.whatsWorking)}
                  onGlintStateChange={(state) =>
                    onSectionGlintStateChange(LATEST_SESSION_FEEDBACK_SECTION_IDS.whatsWorking, state)
                  }
                />
              ) : null}

              {feedbackData.whatToWatchOut ? (
                <WatchOutSection
                  content={feedbackData.whatToWatchOut}
                  showGlowExperience={pendingSections.includes(LATEST_SESSION_FEEDBACK_SECTION_IDS.watchOut)}
                  onGlowExperienceComplete={() => onSectionReveal(LATEST_SESSION_FEEDBACK_SECTION_IDS.watchOut)}
                  onGlintStateChange={(state) =>
                    onSectionGlintStateChange(LATEST_SESSION_FEEDBACK_SECTION_IDS.watchOut, state)
                  }
                />
              ) : null}
            </Box>

            {hasEmotion ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: gridGap,
                  width: '100%',
                  paddingRight: { xs: '0px', sm: '0px', lg: '0px' }
                }}
              >
                {isLargeScreen ? (
                  <LatestFeedbackLayoutBox variant="team" sx={{ width: '100%' }}>
                    <EmotionVisualization
                      emotion={feedbackData.communicatedEmotion.dominantEmotion}
                      title={t('gamer.feedback.emotion.title', 'Your emotional state')}
                      variant="individual"
                      exampleUtterance={feedbackData.communicatedEmotion.exampleUtterance}
                      text={feedbackData.communicatedEmotion.text}
                    />
                  </LatestFeedbackLayoutBox>
                ) : (
                  <EmotionStateSection
                    emotion={feedbackData.communicatedEmotion.dominantEmotion}
                    exampleUtterance={feedbackData.communicatedEmotion.exampleUtterance}
                    text={feedbackData.communicatedEmotion.text}
                    showGlowExperience={pendingSections.includes(LATEST_SESSION_FEEDBACK_SECTION_IDS.emotion)}
                    onGlowExperienceComplete={() => onSectionReveal(LATEST_SESSION_FEEDBACK_SECTION_IDS.emotion)}
                    onGlintStateChange={(state) =>
                      onSectionGlintStateChange(LATEST_SESSION_FEEDBACK_SECTION_IDS.emotion, state)
                    }
                  />
                )}
              </Box>
            ) : null}
          </GamerFeedbackContainerBox>
        </Box>
      </PageWrapper>
    </GlowCursorProvider>
  )
}
